/**
 * Screenshot attachments for the Studio iterate agent ("show, don't
 * describe"). Files arrive via paste (⌘V), drag-drop, or the paperclip
 * picker; each is downscaled client-side to a bounded JPEG data URL so a
 * 5 MB Retina screenshot becomes a ~150–400 KB payload the server accepts
 * and the model reads quickly. Non-image files reject with a readable error.
 */

/** Max screenshots per message — mirrors the server's cap. */
export const MAX_ATTACH_IMAGES = 3;

/** Longest-side bound. 1400 px keeps caption text legible to the model
 *  while staying far under the API's image limits. */
const MAX_DIMENSION = 1400;
const JPEG_QUALITY = 0.85;

/** Read + downscale one image file into a JPEG data URL. */
export function fileToAttachment(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error(`"${file.name || "file"}" isn't an image`));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("canvas unavailable");
        // White backing so transparent PNG regions don't turn black in JPEG.
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`couldn't read "${file.name || "image"}"`));
    };
    img.src = url;
  });
}

/** Pull image files out of a paste event's clipboard (screenshots paste as
 *  files). Empty array = nothing image-like on the clipboard. */
export function imagesFromClipboard(e: ClipboardEvent | React.ClipboardEvent): File[] {
  const items = e.clipboardData?.items;
  if (!items) return [];
  const out: File[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    if (it.kind === "file" && it.type.startsWith("image/")) {
      const f = it.getAsFile();
      if (f) out.push(f);
    }
  }
  return out;
}

/** Image files from a drop event. */
export function imagesFromDrop(e: React.DragEvent): File[] {
  const files = e.dataTransfer?.files;
  if (!files) return [];
  return Array.from(files).filter((f) => f.type.startsWith("image/"));
}
